import {
  AnswerOption,
  AnswerOptionIconDecoration,
  AnswerOptions,
} from '@/features/jobs/questions/components/AnswerOptions'
import { useCallback } from 'react'
import type { Selection } from 'react-aria-components'

interface BoolQuestionAnswerEditorContainerProps {
  value: boolean | null
  onChange: (value: boolean) => unknown
  isDisabled?: boolean
  isInvalid?: boolean
  'aria-labelledby'?: string
  'aria-describedby'?: string
  testId?: string
  yesTestId?: string
  noTestId?: string
}

/**
 * Boolean question answer editor.
 */
export function BoolQuestionAnswerEditor({
  value,
  onChange,
  isDisabled,
  testId,
  yesTestId,
  noTestId,
  ...rest
}: BoolQuestionAnswerEditorContainerProps) {
  const onAnswerOptionsSelectionChange = useCallback(
    (selection: Selection) => {
      if (!(selection instanceof Set)) {
        return
      }

      if (selection.size === 0) {
        return
      }

      const newValue = selection.has('yes')
      onChange(newValue)
    },
    [onChange],
  )

  return (
    <AnswerOptions
      mode="single"
      layout="grid"
      items={items}
      disabledKeys={isDisabled ? allIds : undefined}
      disallowEmptySelection
      selectedKeys={
        value === null ? selectionEmpty : value ? selectionYes : selectionNo
      }
      onSelectionChange={onAnswerOptionsSelectionChange}
      testId={testId}
      {...rest}
    >
      {(item) => (
        <AnswerOption
          withoutSelectionIcon
          id={item.id}
          label={item.label}
          testId={item.id === 'yes' ? yesTestId : noTestId}
          decoration={
            <AnswerOptionIconDecoration
              name={item.id === 'yes' ? 'tick' : 'close'}
            />
          }
        />
      )}
    </AnswerOptions>
  )
}

const items = [box(true), box(false)]
const allIds = items.map((x) => x.id)
const selectionEmpty = new Set<string>([])
const selectionYes = new Set<string>(['yes'])
const selectionNo = new Set<string>(['no'])

function box(value: boolean) {
  return {
    label: value ? 'Yes' : 'No',
    id: value ? 'yes' : 'no',
    value,
  } as const
}
